import React, { useState, useEffect } from 'react'
import { Card, Button, Space, Typography, List, Tag, Progress, Alert, Row, Col, Collapse, Tooltip, Badge, Avatar } from 'antd'
import { BulbOutlined, StarOutlined, DollarOutlined, ClockCircleOutlined, ToolOutlined, CheckCircleOutlined, ArrowUpOutlined, ArrowDownOutlined, EyeOutlined } from '@ant-design/icons'
import { useDesignStore } from '../../store/designStore'
import DesignIntelligence, { DesignSuggestion, DESIGN_STYLES } from '../../services/ai/DesignIntelligence'

const { Title, Text } = Typography
const { Panel } = Collapse

interface DesignIntelligencePanelProps {
  onClose: () => void
}

const DesignIntelligencePanel: React.FC<DesignIntelligencePanelProps> = ({ onClose }) => {
  const { getAllObjects, selectedObjects, updateObjectProperties } = useDesignStore()
  const [suggestions, setSuggestions] = useState<DesignSuggestion[]>([])
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [detectedStyle, setDetectedStyle] = useState<{ style: string; confidence: number; reasoning: string } | null>(null)
  const [appliedSuggestions, setAppliedSuggestions] = useState<Set<string>>(new Set())

  const allObjects = getAllObjects()
  const designIntelligence = new DesignIntelligence(allObjects)

  // Run design analysis
  const runDesignAnalysis = async () => {
    setIsAnalyzing(true)
    
    // Simulate AI processing time
    await new Promise(resolve => setTimeout(resolve, 2500))
    
    // Analyze existing style
    const styleAnalysis = designIntelligence.analyzeExistingStyle()
    setDetectedStyle({
      style: styleAnalysis.detectedStyle,
      confidence: styleAnalysis.confidence,
      reasoning: styleAnalysis.reasoning
    })
    
    // Generate suggestions
    const newSuggestions = designIntelligence.generateDesignSuggestions()
    setSuggestions(newSuggestions)
    
    setIsAnalyzing(false)
  }

  // Auto-run analysis when objects change
  useEffect(() => {
    if (allObjects.length > 0) {
      runDesignAnalysis()
    }
  }, [allObjects.length])

  // Apply a suggestion
  const applySuggestion = (suggestion: DesignSuggestion) => {
    // Mark as applied
    setAppliedSuggestions(prev => new Set([...prev, suggestion.id]))
    
    // Apply the suggestion based on type
    if (suggestion.type === 'material_upgrade' && suggestion.beforeAfter) {
      // Find the object to update (simplified)
      const targetObject = allObjects[0] // For demo, apply to first object
      if (targetObject && targetObject.type === 'ADU') {
        // Update ADU materials
        const updatedMaterials = { ...targetObject.materials }
        updateObjectProperties(targetObject.id, { materials: updatedMaterials } as any)
      } else if (targetObject && targetObject.type === 'Pool') {
        // Update Pool materials  
        const updatedMaterials = { ...targetObject.materials }
        updateObjectProperties(targetObject.id, { materials: updatedMaterials } as any)
      }
    }
    
    console.log('Applied suggestion:', suggestion.title)
  }

  // Get priority color
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return '#ff4d4f'
      case 'medium': return '#faad14'
      case 'low': return '#52c41a'
      default: return '#d9d9d9'
    }
  }

  // Get priority icon
  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'high': return <ArrowUpOutlined style={{ color: '#ff4d4f' }} />
      case 'medium': return <ArrowUpOutlined style={{ color: '#faad14' }} />
      case 'low': return <ArrowDownOutlined style={{ color: '#52c41a' }} />
      default: return null
    }
  }

  // Get suggestion type color
  const getTypeColor = (type: string) => {
    switch (type) {
      case 'style_match': return 'purple'
      case 'material_upgrade': return 'blue'
      case 'feature_addition': return 'green'
      case 'color_harmony': return 'orange'
      case 'budget_optimization': return 'gold'
      default: return 'default'
    }
  }

  // Get suggestion type icon
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'style_match': return '🎨'
      case 'material_upgrade': return '⬆️'
      case 'feature_addition': return '➕'
      case 'color_harmony': return '🌈'
      case 'budget_optimization': return '💰'
      default: return '💡'
    }
  }

  // Group suggestions by type
  const groupSuggestionsByType = (suggestions: DesignSuggestion[]) => {
    const groups: Record<string, DesignSuggestion[]> = {}
    suggestions.forEach(suggestion => {
      if (!groups[suggestion.type]) {
        groups[suggestion.type] = []
      }
      groups[suggestion.type].push(suggestion)
    })
    return groups
  }

  return (
    <Card
      title={
        <Space>
          <BulbOutlined />
          <span>Design Intelligence</span>
        </Space>
      }
      extra={
        <Button type="text" onClick={onClose}>
          ×
        </Button>
      }
      style={{
        position: 'fixed',
        top: '80px',
        left: '480px', // Position next to Smart Placement panel
        width: '500px',
        maxHeight: '85vh',
        overflowY: 'auto',
        zIndex: 1000,
        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        backdropFilter: 'blur(8px)',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
      }}
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        {/* Analysis Button */}
        {allObjects.length === 0 && (
          <Alert
            message="No Objects to Analyze"
            description="Add an ADU or Pool to your design to get intelligent design suggestions."
            type="info"
            showIcon
          />
        )}

        {allObjects.length > 0 && (
          <Button
            type="primary"
            icon={<BulbOutlined />}
            onClick={runDesignAnalysis}
            loading={isAnalyzing}
            block
            size="large"
          >
            {isAnalyzing ? 'Analyzing Design...' : 'Get Design Suggestions'}
          </Button>
        )}

        {/* Analysis Progress */}
        {isAnalyzing && (
          <div>
            <Text type="secondary" style={{ fontSize: '12px', marginBottom: '8px', display: 'block' }}>
              🤖 AI is analyzing your design for style and optimization opportunities...
            </Text>
            <Progress percent={100} showInfo={false} status="active" />
            <Space direction="vertical" style={{ width: '100%', marginTop: '8px' }}>
              <Text style={{ fontSize: '11px', color: '#666' }}>✓ Analyzing architectural style</Text>
              <Text style={{ fontSize: '11px', color: '#666' }}>✓ Evaluating material choices</Text>
              <Text style={{ fontSize: '11px', color: '#666' }}>✓ Checking color harmony</Text>
              <Text style={{ fontSize: '11px', color: '#666' }}>✓ Identifying upgrade opportunities</Text>
              <Text style={{ fontSize: '11px', color: '#666' }}>✓ Generating personalized suggestions</Text>
            </Space>
          </div>
        )}

        {/* Style Analysis Results */}
        {detectedStyle && (
          <Card size="small" style={{ marginBottom: '16px' }}>
            <Title level={5} style={{ margin: 0, marginBottom: '8px' }}>
              🎨 Detected Style
            </Title>
            <Row gutter={16}>
              <Col span={16}>
                <Text strong style={{ fontSize: '16px' }}>
                  {DESIGN_STYLES[detectedStyle.style]?.name || detectedStyle.style}
                </Text>
                <br />
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {DESIGN_STYLES[detectedStyle.style]?.description}
                </Text>
              </Col>
              <Col span={8} style={{ textAlign: 'center' }}>
                <div style={{ 
                  fontSize: '24px', 
                  fontWeight: 'bold', 
                  color: detectedStyle.confidence > 0.7 ? '#52c41a' : detectedStyle.confidence > 0.4 ? '#faad14' : '#ff4d4f'
                }}>
                  {Math.round(detectedStyle.confidence * 100)}%
                </div>
                <Text type="secondary" style={{ fontSize: '10px' }}>
                  Confidence
                </Text>
              </Col>
            </Row>
            <Text style={{ fontSize: '11px', color: '#666', marginTop: '8px', display: 'block' }}>
              {detectedStyle.reasoning}
            </Text>
          </Card>
        )}

        {/* Design Suggestions */}
        {suggestions.length > 0 && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <Title level={5} style={{ margin: 0 }}>
                💡 Design Suggestions ({suggestions.length})
              </Title>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {appliedSuggestions.size} applied
              </Text>
            </div>

            <Collapse size="small" ghost>
              {Object.entries(groupSuggestionsByType(suggestions)).map(([type, typeSuggestions]) => (
                <Panel
                  header={
                    <Space>
                      <span>{getTypeIcon(type)}</span>
                      <Text strong>{type.replace('_', ' ').toUpperCase()}</Text>
                      <Badge count={typeSuggestions.length} size="small" />
                    </Space>
                  }
                  key={type}
                >
                  <List
                    dataSource={typeSuggestions}
                    renderItem={(suggestion) => {
                      const isApplied = appliedSuggestions.has(suggestion.id)
                      return (
                        <List.Item
                          style={{
                            border: `1px solid ${getPriorityColor(suggestion.priority)}`,
                            borderRadius: '6px',
                            marginBottom: '8px',
                            padding: '12px',
                            backgroundColor: isApplied ? 'rgba(82, 196, 26, 0.05)' : 'transparent',
                            opacity: isApplied ? 0.7 : 1
                          }}
                        >
                          <div style={{ width: '100%' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                              <Space>
                                {getPriorityIcon(suggestion.priority)}
                                <Text strong style={{ fontSize: '13px' }}>
                                  {suggestion.title}
                                  {isApplied && <CheckCircleOutlined style={{ color: '#52c41a', marginLeft: '4px' }} />}
                                </Text>
                              </Space>
                              <Space>
                                <Tag color={getPriorityColor(suggestion.priority)} style={{ fontSize: '10px' }}>
                                  {suggestion.priority.toUpperCase()}
                                </Tag>
                                <Tag color={getTypeColor(suggestion.type)} style={{ fontSize: '10px' }}>
                                  {getTypeIcon(suggestion.type)}
                                </Tag>
                              </Space>
                            </div>
                            
                            <Text style={{ fontSize: '12px', color: '#666', marginBottom: '8px', display: 'block' }}>
                              {suggestion.description}
                            </Text>
                            
                            <Text style={{ fontSize: '11px', color: '#1890ff', marginBottom: '8px', display: 'block' }}>
                              💭 {suggestion.reasoning}
                            </Text>
                            
                            {/* Impact Metrics */}
                            <Row gutter={8} style={{ marginBottom: '8px' }}>
                              <Col span={6}>
                                <Tooltip title="Aesthetic Impact">
                                  <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '10px', color: '#666' }}>Aesthetic</div>
                                    <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#52c41a' }}>
                                      {Math.round(suggestion.impact.aesthetic * 100)}%
                                    </div>
                                  </div>
                                </Tooltip>
                              </Col>
                              <Col span={6}>
                                <Tooltip title="Functionality Impact">
                                  <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '10px', color: '#666' }}>Function</div>
                                    <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#1890ff' }}>
                                      {Math.round(suggestion.impact.functionality * 100)}%
                                    </div>
                                  </div>
                                </Tooltip>
                              </Col>
                              <Col span={6}>
                                <Tooltip title="Cost Impact">
                                  <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '10px', color: '#666' }}>Cost</div>
                                    <div style={{ 
                                      fontSize: '12px', 
                                      fontWeight: 'bold', 
                                      color: suggestion.impact.cost > 0 ? '#ff4d4f' : '#52c41a' 
                                    }}>
                                      {suggestion.impact.cost > 0 ? '+' : ''}${Math.abs(suggestion.impact.cost).toLocaleString()}
                                    </div>
                                  </div>
                                </Tooltip>
                              </Col>
                              <Col span={6}>
                                <Tooltip title="Timeline Impact">
                                  <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '10px', color: '#666' }}>Time</div>
                                    <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#faad14' }}>
                                      {suggestion.impact.timeline}d
                                    </div>
                                  </div>
                                </Tooltip>
                              </Col>
                            </Row>
                            
                            {/* Implementation Details */}
                            <div style={{ marginBottom: '8px' }}>
                              <Text style={{ fontSize: '10px', color: '#999' }}>
                                🔧 {suggestion.implementation.difficulty} • ⏱️ {suggestion.implementation.timeRequired}
                              </Text>
                            </div>
                            
                            {/* Action Buttons */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div>
                                {suggestion.implementation.skillsRequired.slice(0, 2).map((skill, i) => (
                                  <Tag key={i} style={{ fontSize: '9px' }}>{skill}</Tag>
                                ))}
                              </div>
                              <Space>
                                <Button
                                  icon={<EyeOutlined />}
                                  onClick={() => {
                                    // TODO: Preview suggestion in 3D scene
                                    console.log('Preview suggestion:', suggestion.title)
                                  }}
                                >
                                  Preview
                                </Button>
                                <Button
                                  type="primary"
                                  onClick={() => applySuggestion(suggestion)}
                                  disabled={isApplied}
                                >
                                  {isApplied ? 'Applied' : 'Apply'}
                                </Button>
                              </Space>
                            </div>
                          </div>
                        </List.Item>
                      )
                    }}
                  />
                </Panel>
              ))}
            </Collapse>
          </div>
        )}

        {/* No suggestions message */}
        {suggestions.length === 0 && !isAnalyzing && allObjects.length > 0 && (
          <Alert
            message="Great Design!"
            description="Your current design looks excellent. No major improvements needed at this time."
            type="success"
            showIcon
          />
        )}

        {/* Info Panel */}
        <div style={{ 
          background: '#f6f8fa', 
          padding: '12px', 
          borderRadius: '6px',
          border: '1px solid #e1e4e8'
        }}>
          <Text strong style={{ fontSize: '12px', color: '#586069' }}>
            🧠 Design Intelligence Features
          </Text>
          <br />
          <Text style={{ fontSize: '11px', color: '#586069' }}>
            • Architectural style detection and analysis
            <br />
            • Material upgrade recommendations with cost impact
            <br />
            • Feature addition suggestions for functionality
            <br />
            • Color harmony optimization
            <br />
            • Budget-conscious alternatives and savings
            <br />
            • Neighborhood compatibility analysis
            <br />
            • Implementation difficulty and timeline estimates
          </Text>
        </div>
      </Space>
    </Card>
  )
}

export default DesignIntelligencePanel
